import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class PlatformService {
  private readonly isBrowserEnv: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowserEnv = isPlatformBrowser(this.platformId);
  }

  isBrowser(): boolean {
    return this.isBrowserEnv;
  }

  getOS(): string {
    if (!this.isBrowserEnv) return 'Server';

    const ua = navigator.userAgent;

    if (/windows phone/i.test(ua)) return 'Windows Phone';
    if (/win/i.test(ua)) return 'Windows';
    if (/android/i.test(ua)) return 'Android';
    if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
    if (/mac/i.test(ua)) return 'macOS';
    if (/linux/i.test(ua)) return 'Linux';

    return 'Unknown';
  }

  getOSVersion(): string {
    if (!this.isBrowserEnv) return 'Server';

    const ua = navigator.userAgent;
    let match;

    switch (this.getOS()) {
      case 'Windows':
        match = ua.match(/Windows NT ([\d.]+)/);
        return match?.[1] ?? 'Unknown';
      case 'Android':
        match = ua.match(/Android ([\d.]+)/);
        return match?.[1] ?? 'Unknown';
      case 'iOS':
        match = ua.match(/OS (\d+[_\d]*) like Mac OS X/);
        return match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
      case 'macOS':
        match = ua.match(/Mac OS X (\d+[_\d]*)/);
        return match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
      default:
        return 'Unknown';
    }
  }

  getBrowser(): string {
    if (!this.isBrowserEnv) return 'Server';

    const ua = navigator.userAgent;

    if (/Edg/i.test(ua)) return 'Edge';
    if (/OPR|Opera/i.test(ua)) return 'Opera';
    if (/Chrome/i.test(ua)) return 'Chrome';
    if (/Safari/i.test(ua)) return 'Safari';
    if (/Firefox/i.test(ua)) return 'Firefox';
    if (/MSIE|Trident/i.test(ua)) return 'Internet Explorer';

    return 'Unknown';
  }

  getDeviceType(): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
    if (!this.isBrowserEnv) return 'unknown';

    const ua = navigator.userAgent;

    if (/Tablet|iPad/i.test(ua)) return 'tablet';
    if (/Mobi|Android/i.test(ua)) return 'mobile';

    return 'desktop';
  }

  getColorScheme(): 'light' | 'dark' | 'unknown' {
    if (!this.isBrowserEnv || !window.matchMedia) return 'unknown';

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';

    return 'unknown';
  }

  hasTouchSupport(): boolean {
    if (!this.isBrowserEnv) return false;

    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      ((navigator as { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0
    );
  }
}

